import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { shownMatchScore } from '@entities/search-run';
import { postCheckComponentKey, type PostCheckScore as PostCheckData } from '@entities/selection';

import styles from './PostCheckScore.module.css';

interface PostCheckScoreProps {
  /** Соответствие запросу — Match Score. `null` — снимка результата нет. */
  matchScore: number | null;
  /** Подтверждённость сведений. `null` — не считалась: «Нет данных». */
  postCheck: PostCheckData | null;
  /** `compact` — карточка четвёрки, `full` — страница автомобиля и сравнение. */
  layout?: 'compact' | 'full' | undefined;
}

/**
 * Две оценки рядом и **никогда не сложенные** (FE-401, MVP_PRD §38.5).
 *
 * «Соответствие запросу 84 · Сведения подтверждены на 61» — законченное
 * утверждение; среднее из этих двух чисел — выдумка. Общего «итогового балла» в
 * продукте не существует, и складывать их нельзя ни здесь, ни где-либо ещё.
 *
 * Четыре правила, каждое из документа и каждое легко нарушить:
 *
 * * **это оценка сведений, а не автомобиля.** Подпись — «Сведения подтверждены
 *   на 61 из 100», а не «автомобиль на 61 из 100». Слова «надёжный»,
 *   «безопасный», «в хорошем состоянии» рядом с числом запрещены (SEC-230);
 * * **под числом обязателен текст о том, что отказ отвечать ничего не
 *   доказывает** (§36.4). Он не примечание и не всплывающая подсказка: молчание
 *   автохауса снижает оценку, и пользователь обязан прочитать, почему это не
 *   приговор автомобилю;
 * * **каждый компонент раскрывается до основания**: какие вопросы, какие
 *   цитаты, какие расхождения его дали. Компонент без основания не показывается
 *   в сумме — он назван неприменимым, а не обнулён;
 * * **`null` — это «Нет данных», а не ноль.** До `deep_check_ready` второй
 *   оценки не существует, и рисовать вместо неё ноль значило бы сказать
 *   «сведения не подтвердились» там, где их ещё не спрашивали.
 */
export function PostCheckScoreBlock({
  matchScore,
  postCheck,
  layout = 'compact',
}: PostCheckScoreProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <section className={styles.wrap} aria-label={t('selection.postCheck.groupLabel')}>
      <dl className={styles.scores}>
        <div className={styles.item}>
          <dt className={styles.label}>{t('selection.postCheck.matchLabel')}</dt>
          <dd className={styles.value}>
            {matchScore === null ? (
              <span className={styles.empty}>{t('common.noData')}</span>
            ) : (
              // Тот же показанный балл, что в кольце карточки (FE-595).
              <span className="tabular">{shownMatchScore(matchScore)}</span>
            )}
          </dd>
        </div>

        {/* Разделитель — не знак операции: две оценки стоят рядом и не
            складываются (§38.5). */}
        <div className={styles.item}>
          <dt className={styles.label}>{t('selection.postCheck.confirmedLabel')}</dt>
          <dd className={styles.value}>
            {postCheck === null ? (
              <span className={styles.empty}>{t('common.noData')}</span>
            ) : (
              <span className="tabular">{Math.round(postCheck.score)}</span>
            )}
          </dd>
        </div>
      </dl>

      {/* Обязательный текст §36.4: отказ отвечать и молчание снижают оценку,
          но доказательством плохого состояния не являются. */}
      {postCheck === null ? null : (
        <p className={styles.disclaimer}>{t('selection.postCheck.refusalNotice')}</p>
      )}

      {postCheck === null || layout === 'compact' ? null : (
        <>
          <button
            type="button"
            className={styles.toggle}
            aria-expanded={open}
            onClick={() => {
              setOpen((value) => !value);
            }}
          >
            {open ? t('selection.postCheck.hide') : t('selection.postCheck.show')}
          </button>
          {open ? <Components postCheck={postCheck} /> : null}
        </>
      )}
    </section>
  );
}

/**
 * Компоненты оценки с весами и основаниями (§38.5).
 *
 * Неприменимый компонент **показывается** и назван неприменимым: «не о чем
 * судить» и «проверили, не подтвердилось» — разные утверждения, и молча убрать
 * строку значило бы выдать первое за второе.
 */
function Components({ postCheck }: { postCheck: PostCheckData }) {
  const { t } = useTranslation();

  return (
    <>
      <ul className={styles.components}>
        {postCheck.components.map((item) => (
          <li key={item.code} className={item.applicable ? undefined : styles.inapplicable}>
            <span className={styles.componentName}>{t(postCheckComponentKey(item.code))}</span>
            <span className={styles.componentValue}>
              {item.applicable && item.value !== null ? (
                <>
                  <span className="tabular">{Math.round(item.value)}</span>
                  {item.weight === null ? null : (
                    <span className={styles.weight}>
                      {' '}
                      {t('selection.postCheck.weight', { value: Math.round(item.weight) })}
                    </span>
                  )}
                </>
              ) : (
                <span className={styles.empty}>{t('selection.postCheck.inapplicable')}</span>
              )}
            </span>
          </li>
        ))}
      </ul>
      <p className={styles.version}>
        {t('selection.postCheck.version', { version: postCheck.algorithm_version })}
      </p>
    </>
  );
}
